import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { OfflineDbService } from './offline-db.service';
import { MediaRecord } from './offline-db.types';
import { ensurePersistence, hasRoomFor } from './offline-quota';
import { NoteVideoRef } from './offline-note-videos';
import { getVideoQuality } from './offline-video-quality';
import { deleteVideoFile, downloadVideoToFile, openVideoFile, probeVideoStorage, sweepOrphanFiles, videoFileName } from './offline-video-files';

export interface RenditionManifestEntry {
  width: number;
  height: number;
  bitrate: number;
  bytes: number;
  label: string;
  url: string;
  // Always store a rendition under THIS, never a version resolved separately — see the note
  // on the server-side type. Every writer takes it from here.
  version: string;
}

export type RenditionStatusName = 'notrequested' | 'building' | 'ready' | 'noteligible' | 'failed';

export interface StaleVideo {
  moduleid: number;
  uploadid: string;
  resourceId: string;
  savedVersion: string;
  liveVersion: string;
}

// What saving a lesson's videos achieved. A lesson only counts as fully saved when
// saved + alreadySaved === total.
export interface LessonVideosResult {
  total: number;
  saved: number;
  alreadySaved: number;
  // Offline copy not built on the server yet (now requested if it had not been). Try again
  // shortly.
  preparing: number;
  // Can never have an offline copy (audio). Not counted against "fully saved".
  notEligible: number;
  failed: number;
  quotaExceeded: boolean;
}

// The quality saved: the sharpest rendition at or below the student's chosen quality (the
// downloads page setting, offline-video-quality.ts), or the smallest available if every
// rendition is above it. A video without the chosen quality therefore gets the nearest lower one.
export function pickLessonRendition(renditions: RenditionManifestEntry[], maxHeight = getVideoQuality()): RenditionManifestEntry | null {
  if (!renditions.length) return null;
  const fitting = renditions.filter((r) => r.height <= maxHeight).sort((a, b) => b.height - a.height);
  if (fitting.length) return fitting[0];
  return renditions.slice().sort((a, b) => a.height - b.height)[0];
}

export interface RenditionsResponse {
  renditions: RenditionManifestEntry[];
  status: RenditionStatusName;
  // Changes whenever the video is reprocessed or its renditions rebuilt, so a saved copy can
  // be recognised as superseded. 'unversioned' only for media that has never been processed.
  version: string;
}

// Saved video: rendition lookups against the API, plus the manifest-and-file bookkeeping for
// copies stored on this device (files in OPFS, see offline-video-files.ts). Video bytes come
// from media.impata.com directly, not environment.ImpataAppAPIRoot (docs/offline-mode-plan.md
// §7 P4), via a raw fetch() inside the writer worker, so JwtInterceptorService never sees them.
@Injectable({
  providedIn: 'root',
})
export class OfflineMediaService {
  constructor(private offlineDb: OfflineDbService, private http: HttpClient) {}

  // GET /offline/renditions — read only, never schedules work. An empty renditions array plus
  // status 'notrequested' means the offline copies have not been built yet and can be asked for;
  // 'noteligible' means this media can never have them (audio) and must not be asked again.
  async getRenditions(moduleid: number, uploadid: string): Promise<RenditionsResponse> {
    const resp: any = await this.http
      .get(environment.ImpataAppAPIRoot + `offline/renditions?moduleid=${moduleid}&uploadid=${uploadid}`)
      .toPromise();
    const version: string = resp?.version ?? 'unversioned';
    // Fall back to the envelope version per entry. A client deployed ahead of the server would
    // otherwise get undefined here and write it straight into the storage key, producing a
    // record nothing could ever find again.
    const renditions: RenditionManifestEntry[] = (resp?.renditions ?? []).map((r: RenditionManifestEntry) => ({ ...r, version: r.version ?? version }));
    return { renditions, status: resp?.status ?? 'notrequested', version };
  }

  // POST /offline/requestrenditions — queues the build. The worker remuxes the MP4s out of the
  // HLS that already exists, so this is cheap; it is still a POST because it schedules work and
  // must not be triggered by merely opening a page.
  async requestRenditions(moduleid: number, uploadid: string): Promise<RenditionStatusName> {
    const body = new HttpParams().set('moduleid', String(moduleid)).set('uploadid', uploadid);
    const resp: any = await this.http
      .post(environment.ImpataAppAPIRoot + `offline/requestrenditions?${body.toString()}`, null)
      .toPromise();
    return resp?.status ?? 'failed';
  }

  private manifestKey(school: string, user: number, resourceId: string, rendition: string, version: string): string {
    return `${school}:${user}:media:${resourceId}:${rendition}:${version}:manifest`;
  }

  // Which saved videos the server has since reprocessed. Needs a network - callers run it on
  // reconnect and when the downloads hub opens. Never throws: a video we cannot reach is
  // simply not reported as stale.
  async findStaleSavedVideos(school: string, user: number): Promise<StaleVideo[]> {
    const manifests = await this.offlineDb.listMediaManifests(school, user).catch(() => []);
    const stale: StaleVideo[] = [];

    for (const manifest of manifests) {
      const moduleid = moduleIdOf(manifest.resourceId);
      const uploadid = manifest.resourceId.slice(manifest.resourceId.indexOf(':') + 1);
      if (moduleid == null || !uploadid) continue;

      try {
        const live = await this.getRenditions(moduleid, uploadid);
        // 'unversioned' means the server has never processed this video, or the copy predates
        // versioning. Either way we cannot actually tell whether it is out of date, and
        // claiming it is produces a notification the student can never clear.
        if (manifest.version === 'unversioned' || live.version === 'unversioned') continue;
        if (live.version !== manifest.version) {
          stale.push({ moduleid, uploadid, resourceId: manifest.resourceId, savedVersion: manifest.version, liveVersion: live.version });
        }
      } catch {
        continue; // unreachable right now; not evidence of staleness
      }
    }

    return stale;
  }

  // Saves one rendition of a video for offline use, streamed straight to a file (see
  // offline-video-files.ts). Replaces any copy already saved for the same video, but only
  // AFTER the new one is complete — a failed update leaves the old copy playable.
  //
  // Still a whole-file, non-ranged download: media.impata.com's CORS policy does not allow the
  // Range header, so an interrupted download restarts from zero. Memory no longer grows with
  // the file, which is what crashed phones.
  async saveVideo(
    rendition: RenditionManifestEntry,
    school: string,
    user: number,
    resourceId: string,
    onProgress?: (bytesReceived: number, totalBytes: number) => void,
  ): Promise<MediaRecord> {
    if (rendition.bytes && !(await hasRoomFor(rendition.bytes))) {
      throw Object.assign(new Error(`not enough storage: need ${rendition.bytes} bytes`), { name: 'QuotaExceededError' });
    }

    const previous = await this.offlineDb.findMediaManifest(school, user, resourceId).catch((): MediaRecord | undefined => undefined);
    const fileName = videoFileName(resourceId, rendition.label, rendition.version);

    const totalBytes = await downloadVideoToFile(rendition.url, school, user, fileName, onProgress).done;

    const manifest: MediaRecord = {
      key: this.manifestKey(school, user, resourceId, rendition.label, rendition.version),
      recordType: 'manifest',
      storage: 'opfs',
      fileName,
      school,
      user,
      resourceId,
      rendition: rendition.label,
      version: rendition.version,
      mimeType: 'video/mp4',
      totalBytes,
      savedAt: Date.now(),
    };
    await this.offlineDb.putMedia(manifest);

    if (previous && previous.key !== manifest.key) {
      await this.deleteSavedVideo(previous);
    }

    // The first save is the moment to ask the browser not to evict this. Best effort, and it
    // asks only once: a browser that already said yes or no is not asked again.
    ensurePersistence().catch(() => undefined);
    return manifest;
  }

  // Saves every video embedded in a lesson's notes. One at a time (each is a large download),
  // never throws, and never touches the notes - a video failing must not undo a note save.
  // A video already saved at the current version is skipped, so tapping Save again only fetches
  // what is missing.
  async saveLessonVideos(
    refs: NoteVideoRef[],
    school: string,
    user: number,
    onProgress?: (videoIndex: number, videoCount: number, percent: number) => void,
  ): Promise<LessonVideosResult> {
    const result: LessonVideosResult = { total: refs.length, saved: 0, alreadySaved: 0, preparing: 0, notEligible: 0, failed: 0, quotaExceeded: false };

    for (const [i, ref] of refs.entries()) {
      try {
        const live = await this.getRenditions(ref.moduleid, ref.uploadid);

        if (live.status === 'noteligible') {
          result.notEligible++;
          continue;
        }
        const rendition = live.status === 'ready' ? pickLessonRendition(live.renditions) : null;
        if (!rendition) {
          // Not built yet: ask for it (a POST, never triggered by just opening a page - R16),
          // unless it is already being built.
          if (live.status === 'notrequested' || live.status === 'failed') {
            await this.requestRenditions(ref.moduleid, ref.uploadid).catch(() => undefined);
          }
          result.preparing++;
          continue;
        }

        const existing = await this.offlineDb.findMediaManifest(school, user, ref.resourceId).catch((): MediaRecord | undefined => undefined);
        // Same version AND same quality: nothing to do. A different quality means the student
        // changed the setting, so it is re-saved (the old copy is replaced only once the new one
        // is complete - see saveVideo).
        if (existing && existing.version === rendition.version && existing.rendition === rendition.label && (await this.openSavedVideo(existing))) {
          result.alreadySaved++;
          continue;
        }

        onProgress?.(i, refs.length, 0);
        await this.saveVideo(rendition, school, user, ref.resourceId, (received, total) => {
          onProgress?.(i, refs.length, total ? Math.floor((received / total) * 100) : 0);
        });
        result.saved++;
      } catch (err) {
        console.error(`[offline] could not save lesson video ${ref.resourceId}`, err);
        result.failed++;
        if ((err as Error)?.name === 'QuotaExceededError') {
          result.quotaExceeded = true;
          break; // the rest will not fit either
        }
      }
    }
    return result;
  }

  // Whether every video in these refs is on the device, so the lesson can show "Saved offline".
  // Called from the (online) lesson page. Anything not found locally is checked with the server
  // only to recognise media that can never be saved (audio), which must not hold the lesson
  // back forever. Any failure answers false: the button then offers to save again, which only
  // fetches what is missing.
  async allLessonVideosSaved(refs: NoteVideoRef[], school: string, user: number): Promise<boolean> {
    for (const ref of refs) {
      const manifest = await this.offlineDb.findMediaManifest(school, user, ref.resourceId).catch((): MediaRecord | undefined => undefined);
      if (manifest) continue;
      const live = await this.getRenditions(ref.moduleid, ref.uploadid).catch((): RenditionsResponse | null => null);
      if (live?.status !== 'noteligible') return false;
    }
    return true;
  }

  // The saved file, for URL.createObjectURL, or null if it cannot be opened right now.
  //
  // NEVER deletes anything on a null. A null here does not mean "gone": openVideoFile also
  // returns null when storage is merely slow to answer (it is time-bounded), and treating that
  // as missing used to delete the manifest — silently "unsaving" a video that was on the disk.
  // Real removal only happens through deleteSavedVideo, from an explicit action.
  async openSavedVideo(manifest: MediaRecord): Promise<File | null> {
    if (manifest.storage !== 'opfs' || !manifest.fileName) return null;
    return openVideoFile(manifest.school, manifest.user, manifest.fileName);
  }

  async deleteSavedVideo(manifest: MediaRecord): Promise<void> {
    if (manifest.fileName) await deleteVideoFile(manifest.school, manifest.user, manifest.fileName);
    await this.offlineDb.deleteMedia(manifest.key);
  }

  // Unenrollment, refund, expulsion: removes saved videos for modules the CURRENT entitlement
  // no longer covers. Called only by OfflineEntitlementService.sweepIfExpired, with the same
  // rule — only on a fresh entitlement, never on an absent or expired one.
  async deleteVideosNotCoveredBy(school: string, user: number, moduleIds: number[]): Promise<void> {
    const manifests = await this.offlineDb.listMediaManifests(school, user);
    for (const manifest of manifests) {
      const moduleid = moduleIdOf(manifest.resourceId);
      if (moduleid == null || moduleIds.includes(moduleid)) continue;
      await this.deleteSavedVideo(manifest);
    }
  }

  // Fire-and-forget housekeeping, run at startup. Removes the old encrypted-chunk video rows
  // the previous design wrote (they can never play again and can be hundreds of MB), and any
  // file left behind by an interrupted download. Never throws.
  async housekeeping(school: string | null, user: number | null): Promise<void> {
    try {
      const removed = await this.offlineDb.deleteLegacyMediaRows();
      if (removed) console.warn(`[offline] removed ${removed} rows of old-format saved video`);
    } catch (err) {
      console.error('[offline] legacy video cleanup failed', err);
    }

    if (!school || !user || !(await probeVideoStorage())) return;
    try {
      const manifests = await this.offlineDb.listMediaManifests(school, user);
      await sweepOrphanFiles(school, user, new Set(manifests.map((m) => m.fileName).filter(Boolean) as string[]));
    } catch (err) {
      console.error('[offline] orphan video cleanup failed', err);
    }
  }
}

// resourceId is `${moduleid}:${uploadid}` - see media.component.ts.
function moduleIdOf(resourceId: string): number | null {
  const split = resourceId.indexOf(':');
  if (split < 0) return null;
  const moduleid = Number(resourceId.slice(0, split));
  return Number.isFinite(moduleid) ? moduleid : null;
}
